import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CalendarRange, ChefHat, CreditCard, LayoutDashboard, Truck, Users } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = [Users, CalendarRange, Truck, ChefHat, CreditCard, LayoutDashboard];

const DEFAULTS = {
  badge: "For multi-unit event caterers",
  title: "Run every unit, venue and booking from one diary.",
  subtitle: "CRM, dynamic quoting, fleet dispatch, subcontractor coordination and Stripe payments — plus a public hub page for your business that turns visitors into enquiries.",
  cta: "Sign in to your diary",
  footer: "CateringDiary.app — the operating diary for event caterers",
  show_businesses: true,
  features: [],
  gallery: [],
  paragraphs: [],
};

export default function Landing() {
  const [companies, setCompanies] = useState([]);
  const [cms, setCms] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/companies`).then((r) => setCompanies(r.data)).catch(() => {});
    axios.get(`${API}/public/platform`).then((r) => setCms(r.data.landing || {})).catch(() => setCms({}));
  }, []);

  const L = { ...DEFAULTS, ...(cms || {}) };
  const features = L.features?.length ? L.features : [];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <header className="sticky top-0 z-20 border-b border-border bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <p className="font-heading text-lg font-black tracking-tight">CateringDiary<span className="text-brand">.app</span></p>
          <Link to="/login" data-testid="login-button" className="bg-ink px-4 py-2 text-sm font-semibold text-white transition-colors duration-150 hover:bg-ink-light">
            Login
          </Link>
        </div>
      </header>

      <section className="border-b border-border bg-ink text-white">
        <div className="mx-auto max-w-6xl px-6 py-20 lg:py-28">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-brand" data-testid="landing-badge">{L.badge}</p>
          <h1 className="mt-4 max-w-3xl font-heading text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="landing-title">
            {L.title}
          </h1>
          <p className="mt-6 max-w-xl text-base text-white/60 md:text-lg" data-testid="landing-subtitle">{L.subtitle}</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/signup" data-testid="hero-signup-cta" className="flex items-center gap-2 bg-brand px-6 py-3 font-heading text-sm font-bold uppercase tracking-wide text-white transition-colors duration-150 hover:bg-brand-dark">
              {L.cta} <ArrowRight className="h-4 w-4" />
            </Link>
            {companies[0] && (
              <Link to={`/hub/${companies[0].slug}`} data-testid="hero-demo-cta" className="flex items-center gap-2 border border-white/30 px-6 py-3 font-heading text-sm font-bold uppercase tracking-wide text-white transition-colors duration-150 hover:bg-white/10">
                View a live business hub
              </Link>
            )}
          </div>
        </div>
      </section>

      {features.length > 0 && (
        <section className="mx-auto max-w-6xl px-6 py-16">
          <h2 className="font-heading text-2xl font-bold tracking-tight">Everything after the enquiry</h2>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <div key={i} className="border border-border bg-white p-6 transition-transform duration-200 hover:-translate-y-[2px]" data-testid={`feature-card-${i}`}>
                  <Icon className="h-5 w-5 text-brand" />
                  <h3 className="mt-3 font-heading text-base font-bold">{f.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{f.text}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {(L.gallery || []).length > 0 && (
        <section className="border-t border-border bg-white" data-testid="landing-gallery">
          <div className="mx-auto max-w-6xl px-6 py-16">
            <h2 className="font-heading text-2xl font-bold tracking-tight">See it in action</h2>
            <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
              {L.gallery.map((url, i) => (
                <img key={i} src={url} alt="" loading="lazy" data-testid={`landing-gallery-img-${i}`} className="w-full border border-border object-cover" />
              ))}
            </div>
          </div>
        </section>
      )}

      {(L.paragraphs || []).length > 0 && (
        <section className="mx-auto max-w-6xl px-6 py-16" data-testid="landing-paragraphs">
          <div className="space-y-4">
            {L.paragraphs.map((p, i) => (
              <div key={i} className="border border-border bg-white p-8" data-testid={`landing-paragraph-${i}`}>
                {p.title && <h2 className="font-heading text-xl font-bold tracking-tight">{p.title}</h2>}
                <p className="mt-3 whitespace-pre-line text-base leading-relaxed text-muted-foreground">{p.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {L.show_businesses !== false && companies.length > 0 && (
        <section className="border-t border-border bg-white" data-testid="businesses-section">
          <div className="mx-auto max-w-6xl px-6 py-16">
            <h2 className="font-heading text-2xl font-bold tracking-tight">Businesses on CateringDiary</h2>
            <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {companies.map((c) => (
                <Link key={c.slug} to={`/hub/${c.slug}`} data-testid={`hub-card-${c.slug}`} className="group border border-border bg-background transition-transform duration-200 hover:-translate-y-[2px]">
                  {c.hero_image && (
                    <div className="h-36 w-full overflow-hidden border-b border-border">
                      <img src={c.hero_image} alt={c.name} className="h-full w-full object-cover" />
                    </div>
                  )}
                  <div className="p-5">
                    <h3 className="font-heading text-base font-bold group-hover:text-brand">{c.name}</h3>
                    <p className="mt-1 text-sm text-muted-foreground">{c.headline}</p>
                    <p className="mt-3 flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.15em] text-brand">Visit hub <ArrowRight className="h-3 w-3" /></p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <footer className="border-t border-border bg-ink py-8 text-center text-xs uppercase tracking-[0.2em] text-white/40" data-testid="landing-footer">
        {L.footer}
      </footer>
    </div>
  );
}
